import { Drawer } from '@/components/shared/Drawer'
import { useState, useEffect } from 'react'
import { Button } from '@/components/shared/atoms/Button'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useUserProfile } from '@/hooks/api/useUsers'
import { Role } from '@wtf/shared'
import Input from '@/components/shared/molecules/CustomInput'

interface NewCollectionDetailsBottomSheetProps {
    open: boolean
    onClose: () => void
    onSubmit: (data: {
        name: string
        isPrivate: boolean
        isPaidSubscriberOnly: boolean
    }) => void
    isLoading?: boolean
}

export const NewCollectionDetailsBottomSheet = ({
    open,
    onClose,
    onSubmit,
    isLoading = false,
}: NewCollectionDetailsBottomSheetProps) => {
    const [name, setName] = useState('')
    const [privacyOption, setPrivacyOption] = useState('private')
    const { user } = useCurrentUser()
    const { data: profile } = useUserProfile(user?.uid)
    const isStylist = profile?.role === Role.STYLIST

    useEffect(() => {
        if (open) {
            setName('')
            setPrivacyOption(isStylist ? 'public' : 'private')
        }
    }, [open, isStylist])

    // If not stylist, force whoCanSee to 'private'
    if (!isStylist && privacyOption !== 'private') {
        setPrivacyOption('private')
    }

    const handleSubmit = () => {
        const payload = {
            name: name.trim(),
            isPrivate: privacyOption === 'private',
            isPaidSubscriberOnly: false, // This is not used for now until paid subscriptions are enabled.
        }
        onSubmit(payload)
    }

    return (
        <Drawer
            triggerContent={null}
            open={open}
            onOpenChange={onClose}
            drawerHeader={{
                title: 'New Collection',
                onClose: onClose,
                onClick: onClose,
            }}
        >
            <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter collection name"
                className="mb-4"
            />

            <div className="mb-4">
                <label className="mb-2 block text-sm font-normal text-[#858585]">
                    Privacy setting
                </label>
                <div className="flex gap-2">
                    {isStylist && (
                        <Button
                            className="px-2 text-xs"
                            variant={
                                privacyOption === 'public' ? 'solid' : 'outline'
                            }
                            onClick={() => setPrivacyOption('public')}
                        >
                            Public
                        </Button>
                    )}
                    <Button
                        className="px-2 text-xs"
                        variant={
                            privacyOption === 'private' ? 'solid' : 'outline'
                        }
                        onClick={() => setPrivacyOption('private')}
                        disabled={!isStylist}
                    >
                        Private
                    </Button>
                </div>
            </div>
            <Button
                onClick={handleSubmit}
                fullWidth
                disabled={isLoading || !name.trim()}
                className="rounded-xl bg-black py-3.5 text-white"
            >
                {isLoading ? 'Creating...' : 'Create'}
            </Button>
        </Drawer>
    )
}
